import type {
  RecurrenceEndMode,
  RecurrenceFrequency,
  RecurrenceMonthlyMode,
  RecurrenceRequestDto,
} from "@/api/appointments";

/**
 * Calendar — the Repeats control's presets, and the rule they build.
 *
 * 🔴 TIMEZONE-FREE AND EFFECT-FREE. Every function here is a pure transform of
 * the form's own wall-clock strings. Preset LABELS are derived at RENDER time
 * from the current start date — never written into state by an effect watching
 * [startDate, startTime]. That effect is banned on this form (see the design
 * note in AppointmentFormModal.tsx): it would also fire on every hydration and
 * on any tz/defaultDate change, and would then need exactly the flag/ref
 * machinery its absence makes unnecessary.
 *
 * The instants are the SERVER's job. This module never converts a wall clock to
 * UTC — it hands the server the typed date, the typed time and the zone id, and
 * the server derives every occurrence with TimeZoneInfo. See the note on
 * `wallClockToUtcIso` in ./tz.ts for why.
 */

/** Two-letter tokens, matching `RecurrenceWeekdayTokens` server-side. */
const WEEKDAY_TOKENS = ["SU", "MO", "TU", "WE", "TH", "FR", "SA"] as const;

const WEEKDAY_NAMES = [
  "Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday",
] as const;

const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
] as const;

export type RepeatPresetId =
  | "none"
  | "daily"
  | "weekday"
  | "weekly"
  | "biweekly"
  | "monthly"
  | "yearly"
  | "custom";

/**
 * The parts of a `YYYY-MM-DD` string, plus its weekday — computed with
 * `Date.UTC` as a proleptic-Gregorian CALCULATOR, never as an instant. That is
 * the same idiom `duration.ts` and `availability.ts:nextDayIso` use, and it is
 * what makes this correct on a daylight-saving transition day.
 */
function partsOf(dateIso: string): { y: number; m: number; d: number; weekday: number } | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(dateIso)) return null;
  const [y, m, d] = dateIso.split("-").map(Number);
  const at = new Date(Date.UTC(y, m - 1, d));
  if (Number.isNaN(at.getTime())) return null;
  return { y, m, d, weekday: at.getUTCDay() };
}

function ordinal(n: number): string {
  if (n === 1 || n === 21 || n === 31) return `${n}st`;
  if (n === 2 || n === 22) return `${n}nd`;
  if (n === 3 || n === 23) return `${n}rd`;
  return `${n}th`;
}

/**
 * The preset row's labels for a given start date, restating the date in words
 * the way every mainstream calendar does. With no usable start date the labels
 * fall back to generic wording rather than naming a weekday that isn't chosen
 * yet — reachable through AI Create+ when the parse found no date.
 */
export function presetLabels(startDateIso: string): Record<RepeatPresetId, string> {
  const p = partsOf(startDateIso);
  const day = p ? WEEKDAY_NAMES[p.weekday] : null;
  return {
    none: "Does not repeat",
    daily: "Daily",
    weekday: "Every weekday (Mon–Fri)",
    weekly: day ? `Weekly on ${day}` : "Weekly",
    biweekly: day ? `Every 2 weeks on ${day}` : "Every 2 weeks",
    monthly: p ? `Monthly on the ${ordinal(p.d)}` : "Monthly",
    yearly: p ? `Yearly on ${MONTH_NAMES[p.m - 1]} ${p.d}` : "Yearly",
    custom: "Custom…",
  };
}

export const PRESET_ORDER: RepeatPresetId[] = [
  "none", "daily", "weekday", "weekly", "biweekly", "monthly", "yearly", "custom",
];

/** The rule shape a preset stands for, before the anchor and end are attached. */
export interface RuleCore {
  frequency: RecurrenceFrequency;
  interval: number;
  byWeekday?: string | null;
  monthlyMode?: RecurrenceMonthlyMode | null;
  monthlyDayOfMonth?: number | null;
  monthlyWeekdayOrdinal?: number | null;
  monthlyWeekday?: string | null;
}

/** The rule a preset builds for a given start date, or null for "none"/"custom". */
export function presetRule(preset: RepeatPresetId, startDateIso: string): RuleCore | null {
  const p = partsOf(startDateIso);
  switch (preset) {
    case "daily":
      return { frequency: "Daily", interval: 1 };
    case "weekday":
      return { frequency: "Weekly", interval: 1, byWeekday: "MO,TU,WE,TH,FR" };
    case "weekly":
      return p ? { frequency: "Weekly", interval: 1, byWeekday: WEEKDAY_TOKENS[p.weekday] } : null;
    case "biweekly":
      return p ? { frequency: "Weekly", interval: 2, byWeekday: WEEKDAY_TOKENS[p.weekday] } : null;
    case "monthly":
      return p
        ? { frequency: "Monthly", interval: 1, monthlyMode: "ByDayOfMonth", monthlyDayOfMonth: p.d }
        : null;
    case "yearly":
      return { frequency: "Yearly", interval: 1 };
    default:
      return null;
  }
}

/**
 * Which preset an existing rule came from, so the edit form re-opens on the row
 * the user picked rather than dropping every saved series into "Custom".
 * Anything the presets cannot express is genuinely custom.
 */
export function presetForRule(rule: RuleCore | null | undefined, startDateIso: string): RepeatPresetId {
  if (!rule) return "none";
  if (rule.interval === 1 && rule.frequency === "Daily") return "daily";
  if (rule.frequency === "Weekly") {
    const days = (rule.byWeekday ?? "").split(",").filter(Boolean).sort().join(",");
    if (rule.interval === 1 && days === "FR,MO,TH,TU,WE") return "weekday";
    const anchorDay = partsOf(startDateIso)?.weekday;
    const single = anchorDay !== undefined ? WEEKDAY_TOKENS[anchorDay] : null;
    if (single && days === single) return rule.interval === 1 ? "weekly" : rule.interval === 2 ? "biweekly" : "custom";
  }
  if (
    rule.frequency === "Monthly" &&
    rule.interval === 1 &&
    rule.monthlyMode === "ByDayOfMonth" &&
    rule.monthlyDayOfMonth === partsOf(startDateIso)?.d
  ) {
    return "monthly";
  }
  if (rule.frequency === "Yearly" && rule.interval === 1) return "yearly";
  return "custom";
}

export interface RecurrenceEnd {
  endMode: RecurrenceEndMode;
  endAfterOccurrences?: number | null;
  endOnLocalDate?: string | null;
}

/**
 * Assembles the wire payload. The anchor is the form's OWN typed start date and
 * time plus the viewer's zone id — handed over as a wall clock, deliberately
 * not converted here.
 */
export function buildRecurrenceRequest(
  core: RuleCore,
  end: RecurrenceEnd,
  anchorLocalDate: string,
  anchorLocalTime: string,
  timeZoneId: string,
): RecurrenceRequestDto {
  return {
    frequency: core.frequency,
    interval: core.interval,
    endMode: end.endMode,
    anchorLocalDate,
    anchorLocalTime,
    timeZoneId,
    byWeekday: core.byWeekday ?? null,
    monthlyMode: core.monthlyMode ?? null,
    monthlyDayOfMonth: core.monthlyDayOfMonth ?? null,
    monthlyWeekdayOrdinal: core.monthlyWeekdayOrdinal ?? null,
    monthlyWeekday: core.monthlyWeekday ?? null,
    endAfterOccurrences: end.endMode === "AfterOccurrences" ? end.endAfterOccurrences ?? null : null,
    endOnLocalDate: end.endMode === "OnDate" ? end.endOnLocalDate ?? null : null,
  };
}

export { WEEKDAY_TOKENS, WEEKDAY_NAMES };
